import { CommonModule } from '@angular/common';
import { Component, WritableSignal, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Timestamp } from '@angular/fire/firestore';
import { FormsModule } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService, fieldSorter } from '@scandium-oy/ngx-scandium';
import { format, startOfDay } from 'date-fns';
import { combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { NoResultsComponent } from 'src/app/components/no-results/no-results.component';
import { SelectTicketsComponent, SelectedTicket } from 'src/app/components/select-tickets/select-tickets.component';
import { Contractor } from 'src/app/models/contractor.model';
import { FileUpload } from 'src/app/models/file.model';
import { Note } from 'src/app/models/note.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { SiteProjectPeriod } from 'src/app/models/site-project-period.model';
import { SiteProject } from 'src/app/models/site-project.model';
import { SiteReport } from 'src/app/models/site-report.model';
import { Site } from 'src/app/models/site.model';
import { RoomTicket } from 'src/app/models/ticket.model';
import { NewNoteComponent } from 'src/app/notes/dialog/new-note.component';
import { TranslateTicketPipe } from 'src/app/pipes/ticket-translate/ticket-translate.pipe';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { AreasService } from 'src/app/services/areas.service';
import { ClientsService } from 'src/app/services/clients.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { NotesService } from 'src/app/services/notes.service';
import { ScrollService } from 'src/app/services/scroll.service';
import { SiteProjectPeriodsService } from 'src/app/services/site-project-period.service';
import { SiteProjectsService } from 'src/app/services/site-projects.service';
import { SiteReportsService } from 'src/app/services/site-report.service';
import { UploadService } from 'src/app/services/upload.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { WorkerTicketsService, getRoomName } from 'src/app/services/worker-tickets.service';
import { DataListComponent, DataListItemDirective } from 'src/app/ui/data-list/data-list.component';
import { ModalShellComponent } from 'src/app/ui/modal-shell/modal-shell.component';
import { StepProgressComponent, StepProgressItem } from 'src/app/ui/step-progress/step-progress.component';
import { Roles } from 'src/app/utility/role';
import { defaultRoom } from 'src/app/utility/room';
import { sortByTimestamp } from 'src/app/utility/sort';
import { convertTimestamp } from 'src/app/utility/time';
import { NewReportNoteDialogComponent, SiteReportNote } from './new-note.dialog';

@Component({
  selector: 'app-new-site-report-dialog',
  templateUrl: './new-site-report.dialog.html',
  styleUrls: ['./new-site-report.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    ModalShellComponent,
    StepProgressComponent,
    DataListComponent,
    DataListItemDirective,
    NoResultsComponent,
    TranslateTicketPipe,
    SelectTicketsComponent,
    FormsModule,
    NewNoteComponent,
  ],
})
export class NewSiteReportDialogComponent {
  private areasService = inject(AreasService);
  private clientsService = inject(ClientsService);
  private contractorsService = inject(ContractorsService);
  private loadingService = inject(LoadingService);
  private _modal = inject(ModalController);
  private modalCtrl = inject(ModalController);
  private notesService = inject(NotesService);
  private scrollService = inject(ScrollService);
  private siteProjectsService = inject(SiteProjectsService);
  private siteProjectPeriodsService = inject(SiteProjectPeriodsService);
  private siteReportsService = inject(SiteReportsService);
  private translate = inject(TranslateService);
  private uploadService = inject(UploadService);
  private usersService = inject(UserSessionService);
  private workerTicketsService = inject(WorkerTicketsService);

  site = input<Site>();

  areas$ = toObservable(this.site).pipe(
    filter((site) => site != null),
    switchMap((site) => this.areasService.getAreas(of(site))),
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  contractors$ = toObservable(this.site).pipe(
    filter((site) => site != null),
    switchMap((site) => this.contractorsService.getList({ site: site.guid })),
    tap((contractors) => {
      const user = this.usersService.currentUserS();
      if (user.role !== Roles.manager && user.contractors?.length > 0) {
        const contractor = contractors.find((c) => c.guid === user.contractors[0]);
        this.selectedContractor.set(contractor);
      }
    }),
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  selectedArea = signal<SiteArea>(null);
  selectedContractor = signal<Contractor>(null);
  selectedProject = signal<SiteProject>(null);
  selectedJob = signal<SiteProjectPeriod>(null);
  selectedTemplate = signal<string>(null);
  notes = signal<SiteReportNote[]>([]);
  selectedTickets = signal<SelectedTicket[]>([]);
  includeImages = signal(true);
  clientName = signal<string>(null);
  clientLogo = signal<string>(null);

  step = signal(1);
  loading = signal(false);

  projects$ = combineLatest([toObservable(this.selectedContractor), toObservable(this.usersService.currentUserS)]).pipe(
    filter(([contractor, user]) => contractor != null || [Roles.manager, Roles.managerWorker].includes(user?.role)),
    switchMap(([contractor]) => this.siteProjectsService.getList({ site: this.site().guid, contractor: contractor?.guid })),
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  jobs$ = toObservable(this.selectedProject).pipe(
    filter((project) => project != null),
    switchMap((project) => this.siteProjectPeriodsService.getList({ project: project.guid })),
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  projectPeriods$ = combineLatest([toObservable(this.selectedProject), toObservable(this.selectedContractor)]).pipe(
    switchMap(([project, contractor]) => {
      if (project?.guid) {
        return this.siteProjectPeriodsService.getList({ project: project.guid });
      }
      if (contractor?.guid) {
        return this.siteProjectPeriodsService.getList({ contractor: contractor.guid });
      }
      return of([]);
    }),
    map((items) => items.sort((left, right) => (left.order ?? 0) - (right.order ?? 0) || fieldSorter(['name'])(left, right))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  tickets$ = combineLatest([toObservable(this.selectedContractor), toObservable(this.usersService.currentUserS)]).pipe(
    filter(([contractor, user]) => contractor != null || [Roles.manager, Roles.managerWorker].includes(user?.role)),
    tap(() => this.loading.set(true)),
    switchMap(([contractor]) => this.workerTicketsService.getAreaRoomTickets(of([this.site()]), contractor?.guid)),
    map((tickets) => tickets.map((rt) => {
      rt.date = startOfDay(rt.history[rt.history.length - 1].timestamp);
      const roomName = getRoomName(rt);
      return Object.assign({}, rt, { selected: false, roomName });
    })),
    tap(() => this.loading.set(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly itemsPerPage = 3;
  readonly defaultRoom = defaultRoom;
  readonly finalStep = 4;
  readonly templates = [
    {
      name: 'tasks',
      disabled: false,
    },
    {
      name: 'faultList',
      disabled: true,
    },
  ];

  role = computed(() => this.usersService.role());
  username = computed(() => this.usersService.currentUserS().displayName);
  readonly creationDate = new Date();
  readonly historyLimit = 3;
  readonly printSummaryItems = computed(() => {
    const summary = this.ticketSummary();
    const contractorName = this.selectedContractor()?.name ?? null;
    const siteSupport = `${this.clientName() || this.site().clientName || this.site().client}${this.selectedArea() ? ` / ${this.selectedArea().name}` : ''}`;
    return [
      {
        label: this.translate.instant('timesheet.site') as string,
        value: this.site().name,
        support: siteSupport,
        wide: true,
      },
      {
        label: this.translate.instant('users.title') as string,
        value: this.username(),
        support: contractorName,
      },
      {
        label: this.translate.instant('kanban.title') as string,
        value: summary.total,
        support: this.translate.instant('report.summary') as string,
      },
      {
        label: this.translate.instant('kanban.status.created') as string,
        value: summary.open,
        support: this.formatHours(summary.estimatedOpenHours),
      },
      {
        label: this.translate.instant('kanban.status.inProgress') as string,
        value: summary.inProgress,
      },
      {
        label: this.translate.instant('kanban.status.done') as string,
        value: summary.done,
        support: summary.hasLoggedTime
          ? this.formatHours(summary.actualDoneHours)
          : null,
      },
    ];
  });

  readonly ticketSummary = computed(() => {
    const tickets = this.selectedTickets() ?? [];
    const open = tickets.filter((ticket) => this.isOpenStatus(ticket.status));
    const inProgress = tickets.filter((ticket) => ticket.status === 'inProgress');
    const done = tickets.filter((ticket) => this.isDoneStatus(ticket.status));
    const hasLoggedTime = done.some((ticket) => Number.isFinite(ticket.usedTimeInH));

    return {
      total: tickets.length,
      open: open.length,
      inProgress: inProgress.length,
      done: done.length,
      estimatedOpenHours: open.reduce((sum, ticket) => sum + (ticket.hours ?? 0), 0),
      actualDoneHours: done.reduce((sum, ticket) => sum + (Number.isFinite(ticket.usedTimeInH) ? ticket.usedTimeInH : 0), 0),
      hasLoggedTime,
    };
  });

  readonly stepItems: StepProgressItem[] = [
    { step: 1, labelKey: 'report.new.steps.template' },
    { step: 2, labelKey: 'report.new.steps.settings' },
    { step: 3, labelKey: 'report.new.steps.tasks' },
    { step: 4, labelKey: 'report.new.steps.summary' },
  ];

  constructor() {
    toObservable(this.site).pipe(
      filter((site) => site != null),
      takeUntilDestroyed(),
    ).subscribe((site) => {
      const syncedClient = this.clientsService.getSync(site.client);
      if (syncedClient?.name) {
        this.clientName.set(syncedClient.name);
      }
      if (syncedClient?.logo) {
        this.clientLogo.set(syncedClient.logo);
      }

      if (syncedClient?.name && syncedClient?.logo) {
        return;
      }

      this.clientsService.get(site.client).pipe(takeUntilDestroyed()).subscribe((client) => {
        if (client?.name) {
          this.clientName.set(client.name);
        }
        if (client?.logo) {
          this.clientLogo.set(client.logo);
        }
      });
    });
  }

  onStep(value: number) {
    this.step.update((val) => val + value);
    this.scrollService.scrollToTop();
  }

  onTemplate(template: string) {
    this.selectedTemplate.set(template);
    this.step.update((val) => ++val);
  }

  onNoteSave(note: Note) {
    note.site = this.site().guid;
    note.sitename = this.site().name;
    if (this.selectedContractor()) {
      note.contractor = this.selectedContractor().guid;
    }
    if (this.selectedArea()) {
      note.room = this.selectedArea().guid;
    }
    this.notesService.save(note).then(() => this.dismiss());
  }

  selectItem(items: unknown[], item: WritableSignal<unknown>) {
    const clearButton = item() != null;

    this.modalCtrl.create({ component: SelectDialogComponent, componentProps: { items, clearButton }, cssClass: ['stack-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            item.set(null);
          } else {
            item.set(data.data);
          }
        }
      });
    });
  }

  removeNote(index: number, name: string) {
    Dialog.confirm({
      message: this.translate.instant('general.delete') + ' ' + name,
      okButtonTitle: this.translate.instant('general.yes'),
      cancelButtonTitle: this.translate.instant('general.no'),
    }).then((dialog) => {
      if (dialog.value) {
        this.notes.update((val) => {
          val.splice(index, 1);
          return val;
        });
      }
    });
  }

  onNewNote() {
    const site = this.site().guid;
    this.modalCtrl.create({ component: NewReportNoteDialogComponent, componentProps: { site }, cssClass: ['stack-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.notes.update((val) => [...val, data.data]);
        }
      });
    });
  }

  onSelectTicket(roomTickets: SelectedTicket[]) {
    roomTickets.map((it) => {
      it.history = it.history.sort((a, b) => sortByTimestamp(a, b, 'timestamp')).reverse();
    });
    this.selectedTickets.set(roomTickets);
  }

  getTicketPhaseName(ticket: RoomTicket, projectPeriods: SiteProjectPeriod[] = []) {
    if (this.selectedJob()?.name) {
      return this.selectedJob().name;
    }
    const period = projectPeriods.find((item) => item.guid === ticket.projectPeriod);
    if (period?.name) {
      return period.name;
    }
    if (ticket.phase != null && ticket.phase > 0) {
      return `Työvaihe ${ticket.phase}`;
    }
    return null;
  }

  getTicketDueDate(ticket: RoomTicket) {
    return this.toDate(ticket.deadline) ?? this.toDate(ticket.date);
  }

  getTicketProjectName(ticket: RoomTicket, projects: SiteProject[] = [], projectPeriods: SiteProjectPeriod[] = []) {
    if (this.selectedProject()?.name) {
      return this.selectedProject().name;
    }
    const period = projectPeriods.find((item) => item.guid === ticket.projectPeriod);
    if (!period?.project) {
      return null;
    }
    return projects.find((item) => item.guid === period.project)?.name ?? null;
  }

  getTicketAssigneeLabel(ticket: RoomTicket) {
    const usernames = ticket.usernames?.filter(Boolean) ?? [];
    if (usernames.length > 0) {
      return usernames.join(', ');
    }
    return ticket.username ?? null;
  }

  getDeadlineOnly(ticket: RoomTicket) {
    return this.toDate(ticket.deadline);
  }

  formatHours(value: number) {
    return `${value.toLocaleString('fi-FI', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
      maximumFractionDigits: 1,
    })} h`;
  }

  getStatusClass(ticket: RoomTicket) {
    if (this.isDoneStatus(ticket.status)) {
      return 'a4-pdf-card__status-pill--done';
    }
    if (ticket.status === 'inProgress') {
      return 'a4-pdf-card__status-pill--in-progress';
    }
    if (ticket.status === 'blocked' || ticket.status === 'late' || ticket.status === 'declined') {
      return 'a4-pdf-card__status-pill--blocked';
    }
    if (ticket.status === 'paused') {
      return 'a4-pdf-card__status-pill--paused';
    }
    return 'a4-pdf-card__status-pill--open';
  }

  private isDoneStatus(status: string) {
    return ['done', 'checked'].includes(status);
  }

  private isOpenStatus(status: string) {
    return !this.isDoneStatus(status) && status !== 'inProgress';
  }

  onDone() {
    const contractor = this.selectedContractor();
    const site = this.site();
    this.loadingService.showLoading(undefined, 'general.printing', ['full-loading']);
    const name = `tehtäväraportti_${site.name}_${contractor?.name ?? 'manager'}_${this.creationDate.toISOString()}`;
    const title = this.translate.instant('report.new.list.tasks') as string;
    const dateLabel = format(this.creationDate, 'd.M.yyyy');
    this.siteReportsService.print({
      printId: 'print-taskreport',
      site,
      reportName: this.selectedTemplate() ?? 'tasks',
      creatorName: contractor?.name ?? 'manager',
      createdAt: this.creationDate,
      clientName: this.clientName() ?? this.site().clientName ?? this.site().client,
      tickets: this.selectedTickets(),
      includeImages: this.includeImages(),
      historyLimit: this.historyLimit,
    }, {
      output: true,
      title,
      dateLabel,
      brandName: this.clientName() ?? this.site().clientName ?? this.site().client,
      logoUrl: this.clientLogo(),
    })
      .then((blob) => {
        if (blob) {
          const file = new File([blob], `${name}.pdf`);
          const fileupload = new FileUpload(file);
          fileupload.guid = site.guid;
          this.uploadService.uploadImage(fileupload).then((url) => {
            const siteReport: SiteReport = {
              name: this.selectedTemplate(),
              site: site.guid,
              contractor: { guid: contractor?.guid ?? Roles.manager, name: contractor?.name ?? '' },
              url,
              created: Timestamp.now(),
              creator: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
              tickets: this.selectedTickets().map((it) => ({ guid: it.guid, parentGuid: it.parent?.guid ?? null, name: it.name })),
            };
            this.siteReportsService.save(siteReport).then(() => this.dismiss());
          });
        } else {
          this.loadingService.hideLoading();
        }
      }, (e) => {
        console.error(e);
        this.loadingService.hideLoading();
      });
  }

  dismiss() {
    this.loadingService.hideLoading();
    this._modal.dismiss();
  }

  private toDate(value: Date | Timestamp | string | number | null | undefined) {
    const date = convertTimestamp(value);
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
}
